/** Shared constants for Comparison Blog form + preview tool. */

export const HOUSE_COMPARISON_CATEGORY = "House Comparison";

export const SELECTIVE_MARLAS = [
  2, 2.5, 3, 4, 5, 6, 7, 8, 9, 10, 12, 14, 16, 18, 20, 32, 40, 60, 80,
];

export const MARLA_OPTIONS = SELECTIVE_MARLAS.map((m) => {
  const n = Number(m);
  const isKanal = Number.isInteger(n) && n >= 20 && n % 20 === 0;
  const kanal = isKanal ? n / 20 : null;
  const kStr = kanal ? ` / ${kanal} Kanal` : "";
  return {
    key: String(m),
    marla: n,
    sqYards: n * 25,
    label: `${m} Marla${kStr}`,
    labelSqYards: `${n * 25} Sq Yards`,
  };
});

/** Dropdown / banner label for a marla key based on display mode. */
export const sizeOptionLabel = (opt, mode = "marla") =>
  mode === "sqYards" ? opt.labelSqYards : opt.label;

export const sizeDisplayFromMarla = (marla, mode = "marla") => {
  const n = Number(marla);
  if (!Number.isFinite(n)) return "";
  if (mode === "sqYards") return `${n * 25} Square Yards`;
  return `${n} Marla`;
};

export const HOUSE_TYPES = [
  {
    id: 1,
    storey: "Single storey",
    floors: "Ground and Mumty floor",
    label: "Single Storey — Ground + Mumty",
    titleLabel: "Single Storey",
    storey_ur: "سنگل اسٹوری",
    floors_ur: "گراؤنڈ اور ممٹی فلور",
    titleLabel_ur: "سنگل اسٹوری",
    urlStorey: "single-storey",
    urlFloors: "g-m", // Ground + Mumty
  },
  {
    id: 2,
    storey: "Double storey",
    floors: "Ground and First floor",
    label: "Double Storey — Ground + First + Mumty",
    titleLabel: "Double Storey",
    storey_ur: "ڈبل اسٹوری",
    floors_ur: "گراؤنڈ اور فرسٹ فلور",
    titleLabel_ur: "ڈبل اسٹوری",
    urlStorey: "double-storey",
    urlFloors: "g-f", // Ground + First
  },
  {
    id: 3,
    storey: "Double storey",
    floors: "Basement and Ground floor",
    label: "Double Storey — Basement + Ground + Mumty",
    titleLabel: "Double Storey",
    storey_ur: "ڈبل اسٹوری",
    floors_ur: "بیسمنٹ اور گراؤنڈ فلور",
    titleLabel_ur: "ڈبل اسٹوری",
    urlStorey: "double-storey",
    urlFloors: "b-g", // Basement + Ground
  },
  {
    id: 4,
    storey: "Triple storey",
    floors: "Ground, First and Second floor",
    label: "Triple Storey — Ground + First + Second + Mumty",
    titleLabel: "Triple Storey",
    storey_ur: "ٹرپل اسٹوری",
    floors_ur: "گراؤنڈ، فرسٹ اور سیکنڈ فلور",
    titleLabel_ur: "ٹرپل اسٹوری",
    urlStorey: "triple-storey",
    urlFloors: "g-f-s", // Ground + First + Second
  },
  {
    id: 5,
    storey: "Triple storey",
    floors: "Basement, Ground and First floor",
    label: "Triple Storey — Basement + Ground + First + Mumty",
    titleLabel: "Triple Storey",
    storey_ur: "ٹرپل اسٹوری",
    floors_ur: "بیسمنٹ، گراؤنڈ اور فرسٹ فلور",
    titleLabel_ur: "ٹرپل اسٹوری",
    urlStorey: "triple-storey",
    urlFloors: "b-g-f", // Basement + Ground + First
  },
  {
    id: 6,
    storey: "Four storey",
    floors: "Basement, Ground, First and Second floor",
    label: "Four Storey — Basement + Ground + First + Second + Mumty",
    titleLabel: "Four Storey",
    storey_ur: "فور اسٹوری",
    floors_ur: "بیسمنٹ، گراؤنڈ، فرسٹ اور سیکنڈ فلور",
    titleLabel_ur: "فور اسٹوری",
    urlStorey: "four-storey",
    urlFloors: "b-g-f-s", // Basement + Ground + First + Second
  },
];

export const QUALITIES = [
  {
    key: "budget",
    label: "Budget Quality",
    descLabel: "Budget",
    label_ur: "بجٹ کوالٹی",
    descLabel_ur: "بجٹ",
  },
  {
    key: "aQuality",
    label: "A Quality",
    descLabel: "A",
    label_ur: "اے کوالٹی",
    descLabel_ur: "اے",
  },
  {
    key: "premium",
    label: "Premium Quality",
    descLabel: "Premium",
    label_ur: "پریمیم کوالٹی",
    descLabel_ur: "پریمیم",
  },
];

export const QUALITY_STYLE = {
  budget: { bg: "#dbeafe", color: "#1e40af" },
  aQuality: { bg: "#dcfce7", color: "#14532d" },
  premium: { bg: "#fef3c7", color: "#92400e" },
};

const sideParts = (side) => {
  const ht =
    HOUSE_TYPES.find((h) => h.id === Number(side.htId)) || HOUSE_TYPES[0];
  const quality = QUALITIES.find((q) => q.key === side.quality);
  const q = quality?.label || side.quality || "";
  const qDesc = quality?.descLabel || side.quality || "";
  const qUr = quality?.label_ur || q;
  const qDescUr = quality?.descLabel_ur || qDesc;
  const marla = Number(side.marla);
  const sqYards = Number.isFinite(marla) ? marla * 25 : "";
  return { ht, q, qDesc, qUr, qDescUr, marla: side.marla, sqYards };
};

/** Size phrase for EN title/desc — marla or sq yards by mode. Numbers stay Latin. */
const sizePhraseEn = (parts, mode = "marla") =>
  mode === "sqYards"
    ? `${parts.sqYards} Sq Yards`
    : `${parts.marla} Marla`;

const sizePhraseEnLower = (parts, mode = "marla") =>
  mode === "sqYards"
    ? `${parts.sqYards} sq yards`
    : `${parts.marla} marla`;

/** Size phrase for UR title/desc — مرلہ or مربع گز by mode. Numbers stay Latin. */
const sizePhraseUr = (parts, mode = "marla") =>
  mode === "sqYards"
    ? `${parts.sqYards} مربع گز`
    : `${parts.marla} مرلہ`;

/** e.g. marla: "2.5 Marla … House vs 4 Marla … House" | sqYards: "62.5 Sq Yards … House vs 100 Sq Yards … House" */
export const buildTitle = (sideA, sideB, mode = "marla") => {
  const a = sideParts(sideA);
  const b = sideParts(sideB);
  const sideTitle = (parts) =>
    `${sizePhraseEn(parts, mode)} ${parts.ht.titleLabel || parts.ht.storey} ${parts.q} House`;
  return `${sideTitle(a)} vs ${sideTitle(b)}`;
};

/** Numbers stay Latin; rest Urdu. */
export const buildTitleUr = (sideA, sideB, mode = "marla") => {
  const a = sideParts(sideA);
  const b = sideParts(sideB);
  const sideTitle = (parts) =>
    `${sizePhraseUr(parts, mode)} ${parts.ht.titleLabel_ur || parts.ht.storey_ur || parts.ht.storey} ${parts.qUr} گھر`;
  return `${sideTitle(a)} بمقابلہ ${sideTitle(b)}`;
};

export const buildShortDescription = (sideA, sideB, mode = "marla") => {
  const a = sideParts(sideA);
  const b = sideParts(sideB);
  const aSize = sizePhraseEnLower(a, mode);
  const bSize = sizePhraseEnLower(b, mode);

  return (
    `Here we will give comparison of ${aSize}, ${a.ht.storey} (${a.ht.floors}), ${a.qDesc} quality vs. ` +
    `${bSize}, ${b.ht.storey} (${b.ht.floors}), ${b.qDesc} quality house in Pakistan. ` +
    `This only includes cost of constructions of ${aSize}, ${a.ht.storey} (${a.ht.floors}) and ` +
    `${bSize}, ${b.ht.storey} (${b.ht.floors}) and doesn’t include plot price.`
  );
};

/** Numbers stay Latin; rest Urdu. */
export const buildShortDescriptionUr = (sideA, sideB, mode = "marla") => {
  const a = sideParts(sideA);
  const b = sideParts(sideB);
  const aSize = sizePhraseUr(a, mode);
  const bSize = sizePhraseUr(b, mode);

  return (
    `یہاں ہم ${aSize}، ${a.ht.storey_ur} (${a.ht.floors_ur})، ${a.qDescUr} کوالٹی بمقابلہ ` +
    `${bSize}، ${b.ht.storey_ur} (${b.ht.floors_ur})، ${b.qDescUr} کوالٹی گھر کا پاکستان میں موازنہ دیں گے۔ ` +
    `اس میں صرف ${aSize}، ${a.ht.storey_ur} (${a.ht.floors_ur}) اور ` +
    `${bSize}، ${b.ht.storey_ur} (${b.ht.floors_ur}) کی تعمیر کی لاگت شامل ہے، پلاٹ کی قیمت شامل نہیں۔`
  );
};

/**
 * Custom URL keeps floor uniqueness as short codes (title stays short without floors).
 * e.g. "5-marla-four-storey-b-g-f-s-budget-quality-house-vs-8-marla-four-storey-b-g-f-s-premium-quality-house/"
 */
export const buildCustomUrl = (sideA, sideB, mode = "marla") => {
  const a = sideParts(sideA);
  const b = sideParts(sideB);

  const sizeSlug = (parts) => {
    if (mode === "sqYards") {
      return `${String(parts.sqYards).replace(/\./g, "-")}-sq-yards`;
    }
    return `${String(parts.marla).replace(/\./g, "-")}-marla`;
  };

  const qualitySlug = (label) =>
    slugify(label).replace(/-house$/, "") || "quality";

  const sideSlug = (parts) => {
    const storey = parts.ht.urlStorey || slugify(parts.ht.titleLabel || parts.ht.storey);
    const floors = parts.ht.urlFloors || "";
    const quality = qualitySlug(parts.q);
    return [sizeSlug(parts), storey, floors, quality, "house"]
      .filter(Boolean)
      .join("-");
  };

  const slug = `${sideSlug(a)}-vs-${sideSlug(b)}`;
  return slug ? `${slug}/` : "";
};

export const isSideComplete = (side) =>
  Boolean(side?.marla && side?.htId && side?.quality);

export const pickTable = (tables, htId, quality) =>
  (tables || []).find(
    (t) => Number(t.htId) === Number(htId) && t.quality === quality,
  ) || null;

export const formatPkr = (val) => {
  if (val == null || Number.isNaN(Number(val))) return "—";
  const n = Math.round(Number(val));
  const sign = n < 0 ? "-" : "";
  return `${sign}PKR ${Math.abs(n).toLocaleString("en-PK")}`;
};

export const formatPct = (val) => {
  if (val == null || Number.isNaN(Number(val)) || !Number.isFinite(Number(val))) {
    return "—";
  }
  const n = Number(val);
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(1)}%`;
};

export const slugify = (text) =>
  String(text || "")
    .toLowerCase()
    .trim()
    .replace(/\./g, "-") // 2.5 → 2-5, 62.5 → 62-5
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
